import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, ArrowLeft } from "lucide-react";

import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { AppButton } from "components/button/AppButton";
import { routePath } from "config/routeMgt/routePath";
import { useGetPendingFeedback } from "features/feedback/hooks/useGetPendingFeedback";

export const JobOffersListPage = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetPendingFeedback();

  const [search, setSearch] = useState("");

  const jobs = useMemo(() => {
    if (!data) return [];

    console.log("Data type:", typeof data);
    console.log("Is data an array?", Array.isArray(data));
    console.log("Data length:", data.length);
    console.log("Data contents:", data);

    const uniqueMap = new Map();

    data.forEach((job: any) => {
      if (!uniqueMap.has(job.id)) {
        uniqueMap.set(job.id, job);
      }
    });

    return Array.from(uniqueMap.values());
  }, [data]);

  const filteredJobs = jobs.filter((job: any) => {
    const query = search.toLowerCase();

    return (
      job.title?.toLowerCase().includes(query) ||
      job.company_name?.toLowerCase().includes(query) ||
      job.location_city?.toLowerCase().includes(query)
    );
  });

  const formatDate = (date?: string) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleViewDetails = (jobId: string) => {
    navigate(`${routePath.confirmJobOffer}/${jobId}`);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-24 text-base font-medium">
          Loading invites...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div
            onClick={() => navigate(`${routePath.home}?skipRedirect=true`)}
            className="flex w-fit cursor-pointer items-center gap-2 text-[#344054] hover:text-[#101828]"
          >
            <ArrowLeft size={20} />
            <span className="text-lg font-semibold">Back to Dashboard</span>
          </div>
        </div>

        <div className="mb-8 p-6 text-white shadow-lg">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium text-black">
                <span className="h-2 w-2 rounded-full bg-[#22C55E]" />
                Pending confirmations
              </div>

              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">
                Job Offer Invites
              </h1>
            </div>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-[#EAECF0] bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]"
              size={18}
            />

            <input
              type="text"
              placeholder="Search invites by job title, company, or location"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-[#D0D5DD] py-3 pl-10 pr-4 text-sm outline-none transition-all focus:border-[#000B7A] focus:ring-2 focus:ring-[#000B7A]/10"
            />
          </div>

          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <div className="text-right">
              <p className="text-xs font-medium uppercase tracking-wide text-[#98A2B3]">
                Total invites
              </p>

              <p className="text-lg font-bold text-[#101828]">
                {filteredJobs.length}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {filteredJobs.map((job: any) => (
            <div
              key={job.id}
              className="rounded-2xl border border-[#EAECF0] bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-1 gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-lg font-bold text-white shadow-md">
                    {job.company_name
                      ?.split(" ")
                      .map((word: string) => word[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase() || "CS"}
                  </div>

                  <div className="flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-bold leading-tight text-[#101828]">
                        {job.title}
                      </h2>

                      <span className="rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-semibold text-[#4338CA]">
                        Job Offer
                      </span>
                    </div>

                    <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-[#667085]">
                      <span className="font-semibold text-[#344054]">
                        {job.company_name}
                      </span>

                      <span className="text-[#D0D5DD]">•</span>

                      <div className="flex items-center gap-1">
                        <MapPin size={14} className="text-[#667085]" />
                        <span>
                          {job.location_city}, {job.location_country}
                        </span>
                      </div>

                      <span className="text-[#D0D5DD]">•</span>

                      <span className="capitalize font-medium">{job.type}</span>
                    </div>

                    <p className="max-w-2xl text-sm leading-relaxed text-[#475467]">
                      <span className="font-semibold text-[#344054]">
                        {job.company_name}
                      </span>{" "}
                      has indicated that you received an offer for the
                      <span className="font-semibold text-[#344054]">
                        {" "}
                        {job.title}
                      </span>{" "}
                      position. Review the details carefully and confirm the
                      status of this offer.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-start gap-3 lg:items-end lg:justify-center">
                  <div className="min-w-[140px] rounded-xl border border-[#EAECF0] bg-[#F9FAFB] px-4 py-3 text-left lg:text-right">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#98A2B3]">
                      Date Sent
                    </p>

                    <p className="mt-1 text-sm font-bold text-[#101828]">
                      {formatDate(job.created_at)}
                    </p>
                  </div>

                  <button
                    onClick={() => handleViewDetails(job.id)}
                    className="w-full rounded-full bg-[#000B7A] px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-[#00095F] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#000B7A]/20 lg:w-auto"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};
